/**
 * ReportsPage - exports du client (refonte v2026.06) :
 *   1. Rapport CyFun (PDF + Word) — identique à l'onglet CyFun.
 *   2. Excel des contrôles (dashboard par catégorie + inventaire M365/Azure/Manuel/Custom).
 *   3. JSON des preuves de tous les scans (M365 + Azure).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Card, Title3, Body1, Caption1, Button, tokens,
  MessageBar, MessageBarBody, Dropdown, Option, Spinner,
} from '@fluentui/react-components';
import {
  ShieldCheckmark24Regular, DocumentTable24Regular, Code24Regular, ArrowDownload24Regular,
  LockClosed24Regular, Sparkle24Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useLicense } from '../auth/LicenseGate';
import { PageHeader } from '../components/PageHeader';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useLatestScan } from '../hooks/useScans';
import { azureConnectionsApi } from '../api/azureConnectionsApi';
import { azureScansApi } from '../api/azureScansApi';
import { onboardingApi } from '../api/onboardingApi';
import { cyfunApi } from '../api/cyfunApi';
import { manualDecisionsApi } from '../api/manualDecisionsApi';
import { controls, familyLabels } from '../domain/controls';
import { AZURE_CONTROLS } from '../domain/azureControls';
import { MANUAL_GOVERNANCE_CONTROLS } from '../domain/manualControls';
import { CYFUN_LEVELS, type CyFunLevel } from '../domain/cyfunFramework';
import { buildScanByControl, buildCyFunReport, buildControlInventory, type ScanHit } from '../reports/cyfunReportData';
import { downloadCyFunReportPdf, downloadCyFunReportDocx } from '../reports/generateCyFunReport';
import { downloadCisReportPdf, downloadCisReportDocx, type ReportLang } from '../reports/generateCisReport';
import { downloadControlsXlsx, type XlsxControlRow, type XlsxCyFunRow } from '../reports/generateControlsXlsx';
import { downloadEvidenceJson } from '../reports/generateEvidenceJson';

type Job = 'cyfun' | 'cis-m365' | 'cis-azure' | 'xlsx' | 'json' | null;
const REPORT_LANGS: ReportLang[] = ['fr-BE', 'nl-BE', 'de-BE', 'en'];
const REPORT_LANG_LABELS: Record<ReportLang, string> = {
  'fr-BE': '🇧🇪 Français',
  'nl-BE': '🇧🇪 Nederlands',
  'de-BE': '🇧🇪 Deutsch',
  'en':    '🇬🇧 English',
};

function frStatus(s?: string): string {
  const v = (s ?? '').toLowerCase();
  if (v === 'pass') return 'Conforme';
  if (v === 'fail' || v === 'error') return 'Non conforme';
  if (v === 'warning' || v === 'warn') return 'À améliorer';
  if (v === 'manual') return 'Manuel';
  return 'Non évalué';
}

// Exports réservés aux plans Pro et au-dessus (le plan Starter n'a que le Rapport CyFun).
const PRO_OR_HIGHER = new Set(['pro', 'business', 'enterprise']);

export function ReportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const license = useLicense();
  const currentPlan = (license.status?.plan ?? '').toLowerCase();
  // Pendant le chargement de la licence on n'affiche pas le verrou (anti-FOUC).
  const hasProReports = license.loading || Boolean(license.hasLicense && license.status?.valid && PRO_OR_HIGHER.has(currentPlan));
  const { tenant } = useActiveTenant();
  const m365Scan = useLatestScan(tenant.id);

  const [busy, setBusy] = useState<Job>(null);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState<CyFunLevel>('Important');
  // v2026.06.172 - Selecteur langue (FR par defaut). Les references CIS restent en EN.
  const [reportLang, setReportLang] = useState<ReportLang>('fr-BE');

  const connQ = useQuery({ queryKey: ['azure-connections'], queryFn: () => azureConnectionsApi.list(), staleTime: 60_000, retry: false });
  const connId = connQ.data?.connections?.[0]?.id;
  const azureQ = useQuery({ queryKey: ['azure-latest', connId], queryFn: () => azureScansApi.latest(connId!), enabled: !!connId, staleTime: 10_000, retry: false });
  const orgQ   = useQuery({ queryKey: ['onboarding-status'], queryFn: () => onboardingApi.status(), staleTime: 60_000, retry: false });
  const decQ   = useQuery({ queryKey: ['manual-decisions'], queryFn: () => manualDecisionsApi.list(), staleTime: 60_000, retry: false });
  useQuery({ queryKey: ['cyfun-config'], queryFn: () => cyfunApi.getConfig().then((c) => { const tl = c.targetLevel as CyFunLevel; if (CYFUN_LEVELS.includes(tl)) setLevel(tl); return c; }), staleTime: 60_000, retry: false });

  function scanData() {
    const m365Results = (m365Scan as unknown as { results?: Record<string, unknown>[] } | null)?.results ?? [];
    const azureResults = (azureQ.data?.results ?? []) as unknown as Record<string, unknown>[];
    const scanByControl: Map<string, ScanHit> = buildScanByControl(m365Results, azureResults);
    // Superpose les décisions manuelles (ex: DP-012 marqué conforme) sur les scans.
    const decisions = decQ.data?.decisions ?? {};
    for (const [cid, d] of Object.entries(decisions)) {
      const st = d.decision === 'conforme' ? 'pass' : d.decision === 'fail' ? 'fail' : d.decision === 'skip' ? 'not_applicable' : '';
      if (st) scanByControl.set(cid.toLowerCase(), { status: st, hasEvidence: !!d.evidence, date: d.decidedAt, message: d.evidence });
    }
    const o = orgQ.data;
    const orgName = o?.companyLegalName || o?.companyName || tenant.name;
    const company = {
      legalName:  o?.companyLegalName ?? undefined, bce: o?.companyBce ?? undefined,
      address:    o?.companyAddress ?? undefined, postalCode: o?.companyPostalCode ?? undefined,
      city:       o?.companyCity ?? undefined, country: o?.companyCountry ?? undefined,
      contact:    o?.techContact ?? undefined,
    };
    return { scanByControl, orgName, company, m365Results, azureResults };
  }

  async function run(job: Exclude<Job, null>) {
    setBusy(job); setError(null);
    try {
      const { scanByControl, orgName, company } = scanData();
      if (job === 'cyfun') {
        const { rr, meta } = buildCyFunReport(scanByControl, level, { orgName, company });
        const inventory = buildControlInventory(scanByControl);
        downloadCyFunReportPdf(rr, meta, inventory);
        await downloadCyFunReportDocx(rr, meta, inventory);
      } else if (job === 'cis-m365') {
        // v2026.06.172 - Rapport CIS M365 filtre les controls avec ref framework=CIS-M365
        const cisMeta = { orgName, company, date: new Date().toISOString().slice(0, 10), lang: reportLang };
        downloadCisReportPdf('CIS-M365', controls, scanByControl, cisMeta);
        await downloadCisReportDocx('CIS-M365', controls, scanByControl, cisMeta);
      } else if (job === 'cis-azure') {
        // v2026.06.173 - Rapport CIS Azure
        const cisMeta = { orgName, company, date: new Date().toISOString().slice(0, 10), lang: reportLang };
        downloadCisReportPdf('CIS-Azure', AZURE_CONTROLS, scanByControl, cisMeta);
        await downloadCisReportDocx('CIS-Azure', AZURE_CONTROLS, scanByControl, cisMeta);
      } else if (job === 'xlsx') {
        const controlRows: XlsxControlRow[] = [
          ...controls.map((c) => ({ source: 'M365' as const, category: familyLabels[c.family] ?? c.family, id: c.id, title: c.title, status: frStatus(scanByControl.get(c.id.toLowerCase())?.status) })),
          ...AZURE_CONTROLS.map((c) => ({ source: 'Azure' as const, category: c.family, id: c.id, title: c.title, status: frStatus(scanByControl.get(c.id.toLowerCase())?.status) })),
          ...MANUAL_GOVERNANCE_CONTROLS.map((c) => ({ source: 'Manuel' as const, category: c.family, id: c.id, title: c.title, status: scanByControl.has(c.id.toLowerCase()) ? frStatus(scanByControl.get(c.id.toLowerCase())?.status) : 'Manuel' })),
        ];
        const { rr } = buildCyFunReport(scanByControl, level, { orgName, company });
        const cyfunRows: XlsxCyFunRow[] = rr.map((r) => ({ id: r.id, level: r.level, category: r.category, requirement: r.requirement, keyMeasure: r.keyMeasure, type: r.type, conf: r.conf }));
        downloadControlsXlsx({ controlRows, cyfunRows, meta: { orgName } });
      } else if (job === 'json') {
        downloadEvidenceJson({ orgName, m365Scan: m365Scan ?? null, azureScan: azureQ.data ?? null });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <PageHeader title={t('reports.title')} description={t('reports.subtitle', { tenant: tenant.name })} />

      {/* v2026.06.172 - Selecteur langue global pour TOUS les rapports */}
      <Card style={{ padding: 14, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Body1 style={{ fontWeight: 600 }}>Langue des rapports :</Body1>
        <Dropdown
          size="small"
          value={REPORT_LANG_LABELS[reportLang]}
          selectedOptions={[reportLang]}
          onOptionSelect={(_, d) => setReportLang((d.optionValue as ReportLang) ?? 'fr-BE')}
          style={{ minWidth: 200 }}
        >
          {REPORT_LANGS.map((l) => <Option key={l} value={l}>{REPORT_LANG_LABELS[l]}</Option>)}
        </Dropdown>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          Note : les références officielles (CIS, NIS2, ISO, CyFun) restent en anglais.
        </Caption1>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 20, maxWidth: 1080 }}>
        {/* 1. Rapport CyFun */}
        <ReportCard
          icon={<ShieldCheckmark24Regular />}
          accent="#2E8B96"
          title={t('reports.cyfun.title', 'Rapport CyFun')}
          description={t('reports.cyfun.desc', 'Le rapport CyFun complet (PDF + Word) : page de garde, synthèse par catégorie, contrôle par contrôle.')}
          busy={busy === 'cyfun'}
          extra={
            <Dropdown
              size="small"
              value={level}
              selectedOptions={[level]}
              onOptionSelect={(_, d) => setLevel((d.optionValue as CyFunLevel) ?? 'Important')}
              style={{ minWidth: 140, marginBottom: 8 }}
            >
              {CYFUN_LEVELS.map((l) => <Option key={l} value={l}>{l}</Option>)}
            </Dropdown>
          }
          onGenerate={() => void run('cyfun')}
          ctaLabel={t('reports.cyfun.cta', 'Générer (PDF + Word)')}
        />

        {/* 2. Rapport CIS M365 - v2026.06.172 */}
        <ReportCard
          icon={<ShieldCheckmark24Regular />}
          accent="#1F3D5C"
          title="Rapport CIS Microsoft 365"
          description="Tous les contrôles M365 avec leur référence CIS-M365 v3, statut et preuves du dernier scan (PDF + Word)."
          busy={busy === 'cis-m365'}
          onGenerate={() => void run('cis-m365')}
          ctaLabel="Générer CIS M365 (PDF + Word)"
          locked={!hasProReports}
          onUpgrade={() => navigate('/billing')}
        />

        {/* 3. Rapport CIS Azure - v2026.06.173 */}
        <ReportCard
          icon={<ShieldCheckmark24Regular />}
          accent="#0072C6"
          title="Rapport CIS Azure"
          description="Tous les contrôles Azure avec leur référence CIS Azure Foundations Benchmark, statut et preuves (PDF + Word)."
          busy={busy === 'cis-azure'}
          onGenerate={() => void run('cis-azure')}
          ctaLabel="Générer CIS Azure (PDF + Word)"
          locked={!hasProReports}
          onUpgrade={() => navigate('/billing')}
        />

        {/* 4. Excel des contrôles */}
        <ReportCard
          icon={<DocumentTable24Regular />}
          accent="#217346"
          title={t('reports.xlsx.title', 'Excel des contrôles')}
          description={t('reports.xlsx.desc', 'Classeur Excel : dashboard par catégorie + inventaire de tous les contrôles (M365, Azure, Manuel, Custom).')}
          busy={busy === 'xlsx'}
          onGenerate={() => void run('xlsx')}
          ctaLabel={t('reports.xlsx.cta', 'Générer le classeur')}
          locked={!hasProReports}
          onUpgrade={() => navigate('/billing')}
        />

        {/* 5. JSON des preuves */}
        <ReportCard
          icon={<Code24Regular />}
          accent="#4A4A6E"
          title={t('reports.json.title', 'Preuves (JSON)')}
          description={t('reports.json.desc', 'Export brut des preuves de tous les scans (M365 + Azure) pour audit externe.')}
          busy={busy === 'json'}
          onGenerate={() => void run('json')}
          ctaLabel={t('reports.json.cta', 'Exporter le JSON')}
          locked={!hasProReports}
          onUpgrade={() => navigate('/billing')}
        />
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginTop: 16 }}>
          <MessageBarBody>{t('reports.errorTitle')} : {error}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}

function ReportCard({ icon, accent, title, description, busy, onGenerate, ctaLabel, extra, locked = false, onUpgrade }: {
  icon: React.ReactNode; accent: string; title: string; description: string;
  busy: boolean; onGenerate: () => void; ctaLabel: string; extra?: React.ReactNode;
  locked?: boolean; onUpgrade?: () => void;
}) {
  return (
    <Card style={{ borderTop: `3px solid ${accent}`, display: 'flex', flexDirection: 'column', gap: 8, padding: 20, opacity: locked ? 0.85 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ display: 'inline-flex', width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center', background: `${accent}1A`, color: accent }}>{icon}</span>
        <Title3 style={{ margin: 0 }}>{title}</Title3>
        {locked && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#A8631A', background: 'rgba(217,132,43,0.15)', padding: '3px 8px', borderRadius: 999 }}>
            <LockClosed24Regular style={{ width: 14, height: 14 }} /> PRO
          </span>
        )}
      </div>
      <Body1 style={{ color: tokens.colorNeutralForeground2, flex: 1 }}>{description}</Body1>
      {!locked && extra}
      {locked ? (
        <>
          <Button appearance="primary" icon={<Sparkle24Regular />} onClick={onUpgrade} style={{ alignSelf: 'flex-start' }}>
            Passer au plan Pro
          </Button>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Cet export est inclus à partir du plan Pro.
          </Caption1>
        </>
      ) : (
        <Button
          appearance="primary"
          icon={busy ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
          onClick={onGenerate}
          disabled={busy}
          style={{ alignSelf: 'flex-start', background: accent, borderColor: accent }}
        >
          {ctaLabel}
        </Button>
      )}
    </Card>
  );
}
